import { useEffect, useRef, useState, useCallback } from 'react';

/* ── data ─────────────────────────────────────────────── */

const breaches = [
  {
    name: 'PlayDapp',
    date: 'February 2024',
    amount: '$290M',
    amountNum: 290,
    description: 'Smart contract access control flaw allowed unauthorized minting of 1.79 billion PLA tokens.',
    tag: 'Smart contract vulnerability',
  },
  {
    name: 'DMM Bitcoin',
    date: 'May 2024',
    amount: '$305M',
    amountNum: 305,
    description: 'Japanese exchange lost 4,502 BTC through compromised private key. Forced to shut down entirely in December 2024.',
    tag: 'Exchange shut down permanently',
  },
  {
    name: 'BtcTurk',
    date: 'June 2024',
    amount: '$55M',
    amountNum: 55,
    description: 'Turkish exchange hot wallets compromised via stolen private keys. Binance helped freeze $5.3M of stolen funds.',
    tag: 'Hot wallet breach',
  },
  {
    name: 'UwU Lend',
    date: 'June 2024',
    amount: '$20M',
    amountNum: 20,
    description: 'Flash loan attack manipulated price oracle. Entire exploit executed within 7 minutes.',
    tag: 'Exploited in 7 minutes',
  },
  {
    name: 'WazirX',
    date: 'July 2024',
    amount: '$235M',
    amountNum: 235,
    description: 'Indian exchange multisig wallet compromised. Attacker planted fake smart contract 8 days before attack. Attributed to Lazarus Group.',
    tag: 'Multisig wallet breach',
  },
  {
    name: 'Radiant Capital',
    date: 'October 2024',
    amount: '$58M',
    amountNum: 58,
    description: 'Attackers bypassed 3-of-11 multisig via malware that altered transaction data on Gnosis Safe interface. Lazarus Group attributed.',
    tag: 'Multisig bypass via malware',
  },
  {
    name: 'Phemex',
    date: 'January 2025',
    amount: '$85M',
    amountNum: 85,
    description: 'Singapore exchange hot wallet breach. Losses revised upward as investigation progressed. Withdrawals suspended for weeks.',
    tag: 'Hot wallet compromise',
  },
  {
    name: 'Bybit',
    date: 'February 2025',
    amount: '$1.4B',
    amountNum: 1400,
    description: 'North Korean hackers (Lazarus Group) compromised a cold wallet via manipulated multisig transaction through Safe{Wallet} developer machine. Largest crypto hack in history.',
    tag: 'Largest crypto hack ever',
    major: true,
  },
  {
    name: 'Infini',
    date: 'February 2025',
    amount: '$49.5M',
    amountNum: 49.5,
    description: 'Stablecoin neobank exploited via developer privilege misuse. Insider/governance failure.',
    tag: 'Developer privilege exploit',
  },
  {
    name: 'Cetus',
    date: 'May 2025',
    amount: '$223M',
    amountNum: 223,
    description: 'DEX on Sui blockchain exploited via spoofed token contracts that tricked liquidity pools. $223M drained at peak impact.',
    tag: 'Spoofed token exploit',
  },
  {
    name: 'Nobitex',
    date: 'June 2025',
    amount: '$90M',
    amountNum: 90,
    description: 'Iranian exchange hacked, funds sent to irrecoverable addresses with anti-government messages. Politically motivated.',
    tag: 'Politically motivated attack',
  },
  {
    name: 'Balancer v2',
    date: 'November 2025',
    amount: '$129M',
    amountNum: 129,
    description: 'DeFi protocol pools exploited — 5 years after launch. Composable stable pools vulnerability.',
    tag: '5 years of hidden vulnerability',
  },
];

const summaryStats = [
  { value: '$2.2B', label: 'Stolen in 2024', source: 'Chainalysis', num: 2.2 },
  { value: '$3.4B', label: 'Stolen in 2025', source: '↑37% YoY', num: 3.4 },
  { value: '303', label: 'Incidents in 2024', source: '', num: 303 },
  { value: '$269.5M', label: 'Smart contract losses', source: 'Hacken, 2024', num: 269.5 },
];

/* ── count-up hook ────────────────────────────────────── */

function useCountUp(end: number, duration = 1600, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const startTime = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      start = eased * end;
      setVal(start);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, active]);
  return val;
}

/* ── timeline node ────────────────────────────────────── */

interface NodeProps {
  breach: (typeof breaches)[number];
  index: number;
  visible: boolean;
}

const TimelineNode = ({ breach, index, visible }: NodeProps) => {
  const isAbove = index % 2 === 0;
  const isMajor = breach.major;
  const cardW = isMajor ? 'w-[300px] sm:w-[340px]' : 'w-[260px] sm:w-[280px]';
  const spacerW = isMajor ? 'w-[300px] sm:w-[340px]' : 'w-[260px] sm:w-[280px]';

  const card = (
    <div
      className={`${cardW} rounded-xl p-5 transition-all duration-300 hover:border-[rgba(255,255,255,0.15)] ${
        isMajor ? 'border-primary/30 shadow-[0_0_30px_hsl(0_88%_63%/0.08)]' : 'border-[rgba(255,255,255,0.08)]'
      }`}
      style={{
        background: 'linear-gradient(to bottom, hsl(0 0% 10%), hsl(0 0% 7%))',
        border: `1px solid ${isMajor ? 'hsl(0 88% 63% / 0.3)' : 'rgba(255,255,255,0.08)'}`,
      }}
    >
      <p className="text-xs text-muted-foreground mb-1 font-numbers">{breach.date}</p>
      <h3 className={`font-bold mb-1 ${isMajor ? 'text-lg' : 'text-base'}`}>{breach.name}</h3>
      <p className={`font-bold text-primary font-numbers mb-2 ${isMajor ? 'text-3xl sm:text-4xl' : 'text-2xl'}`}>
        {breach.amount}
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">{breach.description}</p>
      <span className="inline-block text-[11px] font-medium text-muted-foreground bg-secondary/60 border border-border px-2.5 py-1 rounded-full">
        {breach.tag}
      </span>
    </div>
  );

  return (
    <div
      className={`flex-shrink-0 flex flex-col items-center transition-all duration-500 ${
        visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      {/* Above half */}
      <div className={`flex items-end justify-center h-[310px] sm:h-[340px] pb-4`}>
        {isAbove ? card : <div className={spacerW} />}
      </div>

      {/* Dot on the line */}
      <div className={`relative z-10 rounded-full bg-primary animate-pulse-dot ${isMajor ? 'w-5 h-5' : 'w-3 h-3'}`} />

      {/* Below half */}
      <div className={`flex items-start justify-center h-[310px] sm:h-[340px] pt-4`}>
        {!isAbove ? card : <div className={spacerW} />}
      </div>
    </div>
  );
};

/* ── stats item ───────────────────────────────────────── */

const StatItem = ({ stat, active }: { stat: (typeof summaryStats)[number]; active: boolean }) => {
  const val = useCountUp(stat.num, 1600, active);
  const isInt = Number.isInteger(stat.num) && stat.num > 10;

  let display: string;
  if (stat.value.startsWith('$') && stat.value.includes('B')) {
    display = `$${val.toFixed(1)}B`;
  } else if (stat.value.startsWith('$') && stat.value.includes('M')) {
    display = `$${val.toFixed(1)}M`;
  } else {
    display = isInt ? Math.round(val).toString() : val.toFixed(1);
  }

  return (
    <div className="text-center flex-1 min-w-[140px]">
      <div className="text-3xl md:text-4xl font-bold text-primary font-numbers">{display}</div>
      <p className="text-sm text-foreground mt-1">{stat.label}</p>
      {stat.source && <p className="text-xs text-muted-foreground mt-0.5">{stat.source}</p>}
    </div>
  );
};

/* ── main component ───────────────────────────────────── */

const BreachShowcase = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);

  // Drag state
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const dragTargetRef = useRef(0);
  const dragCurrentRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  // Section visibility
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Stats visibility
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* ── drag-to-scroll with RAF interpolation ── */

  const animateDrag = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = dragTargetRef.current - dragCurrentRef.current;
    if (Math.abs(delta) < 0.5) {
      dragCurrentRef.current = dragTargetRef.current;
      el.scrollLeft = dragCurrentRef.current;
      rafRef.current = null;
      return;
    }
    dragCurrentRef.current += delta * 0.18;
    el.scrollLeft = dragCurrentRef.current;
    rafRef.current = requestAnimationFrame(animateDrag);
  }, []);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const el = scrollRef.current;
      if (!el) return;
      isDragging.current = true;
      dragStartX.current = e.pageX;
      dragScrollLeft.current = el.scrollLeft;
      dragTargetRef.current = el.scrollLeft;
      dragCurrentRef.current = el.scrollLeft;
      el.style.cursor = 'grabbing';
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging.current || !scrollRef.current) return;
      e.preventDefault();
      const dx = e.pageX - dragStartX.current;
      dragTargetRef.current = dragScrollLeft.current - dx;
      if (!rafRef.current) rafRef.current = requestAnimationFrame(animateDrag);
    },
    [animateDrag],
  );

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-20 relative overflow-hidden mt-[40px]" id="breaches">
      {/* Subtle radial bg */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, hsl(0 0% 12% / 0.4) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-primary text-sm uppercase tracking-widest mb-3 font-numbers">The Cost of Insecurity</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-4">
            They thought they were{' '}
            <span className="text-gradient-red font-normal">secure</span> too
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">
            In 2024–2025, hackers stole over $6.1 billion in crypto. These are the most devastating cases.
          </p>
        </div>

        {/* Horizontal scroll timeline */}
        <div
          ref={scrollRef}
          className="overflow-x-auto scrollbar-hide cursor-grab select-none relative"
          style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onMouseMove={onMouseMove}
        >
          <div className="inline-flex gap-3 sm:gap-4 px-4 lg:px-8 relative">
            {/* Central timeline line */}
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-border z-0" />

            {breaches.map((b, i) => (
              <TimelineNode key={b.name} breach={b} index={i} visible={visible} />
            ))}
          </div>
        </div>

        {/* Stats bar */}
        <div className="mt-16 md:mt-20">
          <div className="gradient-red-line h-px w-full max-w-4xl mx-auto mb-8" />
          <div
            ref={statsRef}
            className={`max-w-5xl mx-auto rounded-2xl border border-border p-8 md:p-10 transition-all duration-700 ${
              statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{
              background: 'hsl(0 0% 6%)',
              boxShadow: '0 -8px 40px hsl(0 0% 0% / 0.5)',
            }}
          >
            <div className="flex flex-wrap justify-center gap-8 md:gap-12">
              {summaryStats.map((s) => (
                <StatItem key={s.label} stat={s} active={statsVisible} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BreachShowcase;
